import { WarningOutlined } from '@ant-design/icons';
import { Button, Form, Modal, Popconfirm } from 'antd';
import generatePicker from 'antd/es/date-picker/generatePicker';

import { dateGenerateConfig } from 'app/components/fields/dateGenerateConfig';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useSaveSales, useToggleSaleOpen } from 'app/hooks/data/useSales';
import type { ISalesResponse } from 'app/typings/sales';
import { addDays, isBefore } from 'app/utils/dayjs';

import { MultipleDatePicker } from '../../Collections/CollectionDetail/CollectionSections/MultipleDatePicker/MultipleDatePicker';

import './SaleEditModal.scss';

const DatePicker = generatePicker<Date>(dateGenerateConfig);

const DATE_FORMAT = 'dddd DD MMMM HH:mm';

type SaleEditModalProps = {
  sale: ISalesResponse;
  isOpen: boolean;
  onClose: () => void;
};

export const SaleEditModal = ({
  sale,
  isOpen,
  onClose,
}: SaleEditModalProps) => {
  const [form] = Form.useForm();

  const { mutate: saveSales, isPending } = useSaveSales();
  const { mutate: toggleSaleOpen, isPending: isTogglingSaleOpen } =
    useToggleSaleOpen(sale.id);

  const handleSubmit = (values: ISalesResponse) => {
    const formattedValues = {
      ...sale,
      ...values,
    };

    saveSales(formattedValues);
    onClose();
  };

  const handleToggleSaleOpen = () => {
    toggleSaleOpen();
    onClose();
  };

  const initialValues = {
    startDate: new Date(sale.startDate),
    endDate: new Date(sale.endDate),
    bookableDays: sale.bookableDays,
  };

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={handleSubmit}
      initialValues={initialValues}
      preserve={false}
    >
      {(values) => (
        <Modal
          width={800}
          open={isOpen}
          okText="Save"
          okButtonProps={{ loading: isPending }}
          onOk={form.submit}
          onCancel={onClose}
          title={'Edit current sale'}
        >
          <>
            <Form.Item name="startDate" label="Start">
              <DatePicker
                showTime
                showToday
                showSecond={false}
                format={DATE_FORMAT}
                disabled
              />
            </Form.Item>
            <Form.Item name="endDate" label="End">
              <DatePicker
                showTime
                showToday
                showSecond={false}
                format={DATE_FORMAT}
                disabledDate={(d) => !d || isBefore(d, addDays(new Date(), 1))}
              />
            </Form.Item>

            <Form.Item label="Bookable days" name="bookableDays">
              <MultipleDatePicker value={values.bookableDays} />
            </Form.Item>

            <div className="edit-sale__close-btn">
              <Popconfirm
                title={`This will ${
                  sale.open ? 'close' : 'open'
                } the current sale immediately. Sure?`}
                onConfirm={handleToggleSaleOpen}
              >
                <Button danger={sale.open} loading={isTogglingSaleOpen}>
                  <WarningOutlined />
                  {sale.open ? (
                    <span>Close current sale</span>
                  ) : (
                    <span>Open current sale</span>
                  )}
                </Button>
              </Popconfirm>
            </div>
          </>
        </Modal>
      )}
    </FormLegacyRenderProp>
  );
};
